
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useRef, useState } from "react";
import { shouldBlockAccess } from "@/utils/subscriptionService";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { LoadingState } from "@/components/ui/loading-state";

// Define the user role type to match what's in the database
type UserRole = 'user' | 'group_admin' | 'admin';

export default function AuthGuard({ 
  children, 
  adminRequired = false,
  requiredRole = undefined 
}: { 
  children: React.ReactNode;
  adminRequired?: boolean;
  requiredRole?: UserRole;
}) {
  const {
    user,
    loading,
    isAdmin,
    isCompanyAdmin,
    adminLoading,
    profileLoading,
    adminError,
    profileError,
    refreshAdminStatus,
    refreshUserProfile,
    approvalStatus
  } = useAuth();
  const { isImpersonating } = useImpersonation();
  const location = useLocation();
  const [blocked, setBlocked] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const completedAccessCheckKeyRef = useRef<string | null>(null);

  const shouldCheckSubscription = Boolean(
    user &&
    !adminRequired &&
    location.pathname !== '/user/subscription' &&
    (location.pathname.startsWith('/user/clients') ||
      location.pathname.startsWith('/user/charging') ||
      location.pathname === '/user/dashboard')
  );
  const accessCheckKey = shouldCheckSubscription ? `${user?.id}:${location.pathname}` : null;
  const accessCheckPending = shouldCheckSubscription && (
    checkingAccess || completedAccessCheckKeyRef.current !== accessCheckKey
  );
  
  // Check if access should be blocked due to payment issues
  // Only check for non-admin routes and specific user feature routes
  useEffect(() => {
    if (!shouldCheckSubscription) {
      setCheckingAccess(false);
      return;
    }
    
    const checkAccess = async () => {
      setCheckingAccess(completedAccessCheckKeyRef.current !== accessCheckKey);
      const shouldBlock = await shouldBlockAccess();
      setBlocked(shouldBlock);
      completedAccessCheckKeyRef.current = accessCheckKey;
      setCheckingAccess(false);
    };
    
    if (!loading) {
      checkAccess();
    }
  }, [shouldCheckSubscription, accessCheckKey, loading]);
  
  // Only show the blocking loader on the initial load (no user/profile yet).
  // Background refreshes (e.g. TOKEN_REFRESHED on window focus) must NOT unmount
  // the current page — that would wipe any in-progress form state.
  const awaitingInitialProfile = !!user && profileLoading;
  if (loading || awaitingInitialProfile || (adminRequired && adminLoading && !isAdmin && !isCompanyAdmin) || accessCheckPending) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingState variant="fullPage" message="Loading..." />
      </div>
    );
  }

  const verificationError = profileError ?? (adminRequired && !isAdmin && !isCompanyAdmin ? adminError : null);
  if (verificationError) {
    const message = verificationError instanceof Error
      ? verificationError.message
      : "Access verification failed.";

    return (
      <main className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-md space-y-4 text-center">
          <h1 className="text-xl font-semibold">Unable to verify access</h1>
          <p className="text-sm text-muted-foreground">{message}</p>
          <button
            type="button"
            onClick={() => {
              refreshUserProfile();
              if (adminRequired) refreshAdminStatus();
            }}
            className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Try again
          </button>
        </div>
      </main>
    );
  }

  // Authentication check - redirect to login if no user
  if (!user) {
    console.log("AuthGuard - No user found, redirecting to home");
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  
  // Check user approval status and redirect accordingly
  // CRITICAL SECURITY: Block any inactive user regardless of approval_status
  if (user?.is_active === false) {
    console.log('[AuthGuard] User is inactive - blocking access', { 
      approval_status: approvalStatus,
      email: user.email 
    });
    
    // Check if this is a reactivation scenario (user was previously approved but is now inactive)
    // This happens when subscriptions are cancelled or expire
    if (approvalStatus === 'approved' && !location.pathname.startsWith('/reactivate-account')) {
      console.log('[AuthGuard] Reactivation needed - user was approved but is now inactive');
      return <Navigate to="/reactivate-account" replace />;
    }
    
    // Route to appropriate holding page based on status for users in approval pipeline
    if (approvalStatus === 'pending_manual_approval') {
      return <Navigate to="/registration-pending?type=manual-approval" replace />;
    }
    if (approvalStatus === 'pending_payment') {
      return <Navigate to="/registration-pending?type=payment" replace />;
    }
    if (approvalStatus === 'pending_approval') {
      return <Navigate to="/registration-pending?type=company-approval" replace />;
    }
    
    // Fallback for any other inactive state
    console.error('[AuthGuard] User inactive with unknown status');
    return <Navigate to="/registration-pending?type=inactive" replace />;
  }
  
  // Additional approval status checks for active users
  if (user && approvalStatus === 'pending_payment' && !location.pathname.startsWith('/registration-pending')) {
    return <Navigate to="/registration-pending?type=payment" replace />;
  }
  
  if (user && approvalStatus === 'pending_approval' && !location.pathname.startsWith('/registration-pending')) {
    return <Navigate to="/registration-pending?type=company-approval" replace />;
  }
  
  if (user && approvalStatus === 'pending_manual_approval' && !location.pathname.startsWith('/registration-pending')) {
    return <Navigate to="/registration-pending?type=manual-approval" replace />;
  }
  
  // Defensive: Block users without approval_status set
  if (user && !approvalStatus) {
    console.error('[AuthGuard] User has no approval_status - potential security issue');
    return <Navigate to="/registration-pending?type=error" replace />;
  }
  
  // Block access if payment is required (except for subscription pages)
  if (blocked && location.pathname !== '/user/subscription' && location.pathname !== '/subscription-contact-admin') {
    // Company admins go to billing page, regular users go to contact admin page
    if (isCompanyAdmin) {
      return <Navigate to="/user/subscription" replace />;
    } else {
      return <Navigate to="/subscription-contact-admin" replace />;
    }
  }

  // Platform routes special case - always allow authenticated users
  if (!adminRequired && location.pathname.includes("/platforms")) {
    return <>{children}</>;
  }

  // Admin route protection
  if (adminRequired) {
    // When impersonating, always check the REAL admin status, not impersonated user
    // This allows admins to access admin routes while impersonating
    const hasAdminAccess = isImpersonating 
      ? isAdmin === true // Real admin status always checked
      : (isAdmin === true || isCompanyAdmin === true);
    
    if (!hasAdminAccess) {
      console.log("Admin access denied for path:", location.pathname, 
        {isAdmin, isCompanyAdmin, isImpersonating});
      return <Navigate to="/user" replace />;
    }
  }

  // For standard authenticated routes with no special permissions
  return <>{children}</>;
}
